import { fabric } from 'fabric';
import bind from 'bind-decorator';

import Box from './box';
import Canvas from './canvas';
import StoreWalls from './store_walls';
import SchemaImage from './schema_image';
import View from '../../../views/view';

export const BOUNDARY_OFFSET_X = 30.0;
export const BOUNDARY_OFFSET_Y = 72.0;
const MAP_RATIO = 1.08;
const ZOOM_FACTOR = 1.25;
const ZOOM_PER_METER = 0.2631578947; // 5/19

export default class Map extends View {
  initialize({
    isEditable = false,
    isSelectable = false,
    isScrollZoomable = true,
    isResizeMapContainer = true,
    level = null,
    boxes = null
  }) {
    fabric.perfLimitSizeTotal = 225000000;
    fabric.maxCacheSideLimit = 11000;
    fabric.textureSize = 2048 * 4;

    this.canvas = null;
    this.storage = null;
    this.boxes = null;
    this.schemaImage = null;

    this.canvasWidth = this.$('.map-inner').width();
    if (isResizeMapContainer) {
      this.$('.map-inner').css('height', (this.canvasWidth / MAP_RATIO).round());
    }

    this.canvasHeight = this.$('.map-inner').height();

    this.storeWidth = level?.fabricAdditionals.width || this.$node.data('width');
    this.storeHeight = level?.fabricAdditionals.height || this.$node.data('height');

    this.maxZoom = ZOOM_PER_METER * this.storeWidth;

    this.isEditable = isEditable;
    this.isSelectable = isSelectable;
    this.isScrollZoomable = isScrollZoomable;

    this.widthFactor =
      ((this.canvasWidth - BOUNDARY_OFFSET_X * 2) / this.storeWidth).round(8);
    this.heightFactor =
      ((this.canvasHeight - BOUNDARY_OFFSET_Y * 2) / this.storeHeight).round(8);

    this.factor = [this.widthFactor, this.heightFactor].min();

    this.xOffsetToCenter = this.widthFactor > this.heightFactor ?
      (this.canvasWidth - this.factor * this.storeWidth - BOUNDARY_OFFSET_X * 2) / 2 :
      0;
    this.yOffsetToCenter = this.widthFactor < this.heightFactor ?
      (this.canvasHeight - this.factor * this.storeHeight - BOUNDARY_OFFSET_Y * 2) / 2 :
      0;

    this._renderCanvas();
    this._renderSchemaImage(level?.schemaImageUrl, level?.fabricData);
    if (this.isEditable) {
      this._renderStoreWalls();
    }
    this._renderBoxes(boxes);
    this._renderBounds();

    this._bindZoom();
    this._bindTabs();
  }

  destroy() {
    this.canvas.dispose();
  }

  // public methods
  get isSchemaImage() {
    return !!this.schemaImage.schemaUrl;
  }

  scale(value) {
    return (value * this.factor).round(8);
  }

  scaleX(value) {
    return (value * this.factor).round(8) + BOUNDARY_OFFSET_X + this.xOffsetToCenter;
  }

  scaleY(value) {
    return (value * this.factor).round(8) + BOUNDARY_OFFSET_Y + this.yOffsetToCenter;
  }

  descale(value) {
    return (value / this.factor).round(8);
  }

  descaleX(value) {
    return ((value - BOUNDARY_OFFSET_X - this.xOffsetToCenter) / this.factor).round(8);
  }

  descaleY(value) {
    return ((value - BOUNDARY_OFFSET_Y - this.yOffsetToCenter) / this.factor).round(8);
  }

  setSize(size) {
    this.boxes.forEach(box => {
      box.isDisabled = size ?
        (box.size !== size && size !== 'any') :
        false;
    });
  }

  setActiveBoxIds(ids) {
    this.boxes.forEach(box => {
      box.isDisabled = !ids.includes(box.id);
    });
  }

  // events
  select(box, isSkipDeselect) {
    if (this.canvas.getActiveObject()?.box === box) {
      if (!isSkipDeselect) {
        this.canvas.discardActiveObject();
        this.canvas.requestRenderAll();
      }
    } else {
      this.canvas.setActiveObject(box.fabricNode);
    }
  }

  @bind
  onSelect() {
    if (!this.canvas.getActiveObject().box) { return; }

    if (this.canvas.getActiveObject() ===
        this.canvas.getActiveObject().box.fabricText) {
      this.canvas.setActiveObject(
        this.canvas.getActiveObject().box.fabricNode
      );
    } else {
      this.boxes.forEach(box => box.deselect());
      this.canvas.getActiveObject().box.select();
    }
  }

  @bind
  onDeselect() {
    if (!this.canvas.getActiveObject().box) { return; }

    this.canvas.getActiveObject().box.deselect();
  }

  @bind
  onBoxClick(box) {
    this.$node.trigger('box:click', { boxId: box.id });
  }

  @bind
  onBoxMouseOver(box) {
    this.$node.trigger('box:mouseover', { boxId: box.id });
  }

  @bind
  onBoxMouseOut(box) {
    this.$node.trigger('box:mouseout', { boxId: box.id });
  }

  @bind
  activateBoxesMode() {
    this.canvas
      .discardActiveObject()
      .renderAll();

    this.schemaImage.deactivate();
    this.boxes.forEach(box => box.selectable = true);
  }

  @bind
  activateSchemaMode() {
    this.canvas
      .discardActiveObject()
      .renderAll();

    this.schemaImage.activate();
    this.boxes.forEach(box => box.selectable = false);
  }

  // private methods
  _bindZoom() {
    this.$('.map-inner .controls .zoom-in').on('click', () => {
      this.canvas.applyZoom(null, this.canvas.getZoom() * ZOOM_FACTOR);
    });
    this.$('.map-inner .controls .zoom-out').on('click', () => {
      this.canvas.applyZoom(null, this.canvas.getZoom() / ZOOM_FACTOR);
    });
  }

  _bindTabs() {
    this.$('.nav-link[href="#tabs-boxes"]').on('click', this.activateBoxesMode);
    this.$('.nav-link[href="#tabs-schema"]').on('click', this.activateSchemaMode);
    this.$('.nav-link.active').trigger('click');
  }

  _renderCanvas() {
    this.canvas = new Canvas('storage_map', {
      maxZoom: this.maxZoom,
      isEditable: this.isEditable
    }, {
      width: this.canvasWidth,
      height: this.canvasHeight,
      selection: false,
      noScaleCache: false,
      map: this
    });

    this.canvas
      .on('selection:created', this.onSelect)
      .on('selection:updated', this.onSelect)
      .on('before:selection:cleared', this.onDeselect);
  }

  _renderStoreWalls() {
    const xOffset = this.scaleX(0);
    const yOffset = this.scaleY(0);

    this.storeWalls = new StoreWalls(this, {
      width: this.scale(this.storeWidth) + xOffset * 2,
      height: this.scale(this.storeHeight) + yOffset * 2,
      left: 0,
      top: 0,
      xOffset,
      yOffset
    });
    this.canvas.add(this.storeWalls);
  }

  _renderSchemaImage(schemaImageUrl, fabricData) {
    this.schemaImage = new SchemaImage(
      this.$('[data-map_node="level"]'),
      {
        schemaImageUrl,
        fabricData,
        map: this
      }
    );
  }

  _renderBoxes(boxes) {
    if (boxes) {
      // const currentBox = boxes.find(({ id }) => id === 188);
      // console.log(currentBox);
      // console.log(
      //   boxes.filter(box => {
      //     if (currentBox.width !== box.width || currentBox.height !== box.height) {
      //       return false;
      //     }
      //
      //     return currentBox.fabricData.left === box.fabricData.left &&
      //       currentBox.fabricData.top === box.fabricData.top &&
      //       currentBox.fabricData.angle === box.fabricData.angle;
      //   })
      // );

      this.boxes = boxes
        .sortBy(box => box.state === 'available')
        .map(box => new Box(null, { map: this, ...box }));
    } else {
      this.boxes = this.$('[data-map_node="box"]')
        .filter(':not([data-ignored])')
        .toArray()
        .sortBy(node => $(node).data('state') === 'available')
        .map(node => new Box(node, { map: this }));
    }

    this.boxes.forEach(box => box.render());
  }

  _renderBounds() {
    if (!window.location.href.match(/[&?]bounds/)) { return; }

    this.canvas.on('after:render', () => {
      this.canvas.contextcontainer.strokestyle = '#cdd3dc';

      this.canvas.forEachObject(obj => {
        const bound = obj.getBoundingRect();

        this.canvas.contextContainer.strokeRect(
          bound.left + 0.5,
          bound.top + 0.5,
          bound.width,
          bound.height
        );
      });
    });
  }
}
